import type { ReactElement } from "react";
import { Link } from "react-router-dom";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import {
  HEADLINE_UNIT_STYLE,
  MoneyValue,
} from "@/modules/dashboard/components/redesign/MoneyValue";
import type {
  DashboardHeadlineKind,
  DashboardHeadlineTile,
  DashboardHeadlineTilesProps,
} from "@/modules/dashboard/types/dashboard-analytics.types";

/**
 * Sparkline hue per measure. Sales and profit reuse the two validated chart
 * series colours so a tile and the chart below it agree; loss wears the error
 * token because it *means* bad, not because it is "series 3".
 */
const KIND_COLOR: Record<DashboardHeadlineKind, string> = {
  sales: "var(--chart-sales)",
  profit: "var(--chart-profit)",
  loss: "var(--error-500)",
};

/**
 * Tile sparkline. viewBox-based and fluid so it scales rather than crops in the
 * single-column mobile layout. Decorative only — every number it hints at is
 * printed as text in the tile or in the chart's table view.
 */
function TileSparkline({
  data,
  color,
}: {
  data: number[];
  color: string;
}): ReactElement | null {
  if (data.length < 2) return null;
  const max = Math.max(...data);
  const min = Math.min(...data);
  const span = max - min || 1;
  const d = data
    .map((v, i) => {
      const x = (i / (data.length - 1)) * 100;
      const y = 26 - ((v - min) / span) * 23 - 1.5;
      return `${i === 0 ? "M" : "L"}${x.toFixed(2)},${y.toFixed(2)}`;
    })
    .join(" ");

  return (
    <svg
      viewBox="0 0 100 26"
      preserveAspectRatio="none"
      aria-hidden="true"
      className="mt-3 block h-[26px] w-full"
    >
      <path d={`${d} L100,26 L0,26 Z`} fill={color} fillOpacity="0.10" />
      <path
        d={d}
        fill="none"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}

function HeadlineTile({ tile }: { tile: DashboardHeadlineTile }): ReactElement {
  const t = useTranslations();
  const hasDelta = tile.deltaPct !== undefined;
  const rising = (tile.deltaPct ?? 0) >= 0;
  // Direction × whether up is good: rising sales/profit is good, rising loss
  // is not. Colouring loss green when it grows would be a lie.
  const good = tile.kind === "loss" ? !rising : rising;

  return (
    <div className="flex min-w-0 flex-col rounded-[16px] border border-[color:var(--line2)] bg-[color:var(--card)] p-4">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        {/* Colour key tying the tile to its line in the chart below. */}
        <span
          aria-hidden="true"
          className="h-[9px] w-[9px] shrink-0 rounded-[3px]"
          style={{ background: KIND_COLOR[tile.kind] }}
        />
        <span className="text-[12.5px] font-bold text-[color:var(--text-secondary)]">
          {tile.label}
        </span>
        {tile.estimatedLabel ? (
          <span className="inline-flex items-center rounded-full border border-[color:var(--tip-br)] bg-[color:var(--tip-bg)] px-2 py-px text-[10.5px] font-bold text-[color:var(--tip-fg)]">
            {tile.estimatedLabel}
          </span>
        ) : null}
      </div>

      {/* Proportional figures, not tabular — these are large standalone
          numbers, and equal-width digits make them look loose. */}
      <p className="mt-2 truncate text-[24px] font-bold leading-[1.3] tracking-[-0.015em] text-[color:var(--text-primary)] sm:text-[27px]">
        {/* The currency symbol is a UNIT, not three more glyphs of the figure:
            it renders at roughly half the size, muted, one weight down, and is
            held off the number by a logical margin. */}
        <MoneyValue {...tile.value} unitStyle={HEADLINE_UNIT_STYLE} />
      </p>

      {hasDelta ? (
        <p
          className="mt-2 truncate text-[11.5px] font-semibold"
          style={{ color: good ? "var(--success-text)" : "var(--error-500)" }}
        >
          <bdi>
            {rising ? "+" : "−"}
            {Math.abs(tile.deltaPct ?? 0).toFixed(1)}%
          </bdi>{" "}
          {t(keys.dashboard.kpiVsPrevious)}
        </p>
      ) : null}

      {tile.meta ? (
        <p className="mt-1 truncate text-[11.5px] text-[color:var(--text-muted)]">
          {tile.meta}
        </p>
      ) : null}

      <TileSparkline data={tile.series} color={KIND_COLOR[tile.kind]} />

      {tile.hint ? (
        <p className="mt-2 text-[11px] leading-[1.45] text-[color:var(--text-secondary)]">
          {tile.hint}{" "}
          {tile.hintHref && tile.hintCta ? (
            <Link
              to={tile.hintHref}
              className="font-semibold text-[color:var(--text-primary)] underline underline-offset-2"
            >
              {tile.hintCta}
            </Link>
          ) : null}
        </p>
      ) : null}
    </div>
  );
}

/**
 * The three headline measures — sales, profit, loss — at equal weight.
 *
 * Mobile-first: one column at 375px, three across from `sm` up. Profit never
 * presents an inflated number as fact; when some sold items carry no recorded
 * cost the caller passes `estimatedLabel` + `hint` and the tile says so.
 */
export function DashboardHeadlineTiles({
  tiles,
}: DashboardHeadlineTilesProps): ReactElement {
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
      {tiles.map((tile) => (
        <HeadlineTile key={tile.kind} tile={tile} />
      ))}
    </div>
  );
}
